// Player settings: one JSON record in localStorage, read at match start. URL params stay dev overrides
// and always win (?quality, ?fog, ?style). The player's name is NOT here — it keeps its own store (nameStore.ts).
import { detectLang, LANGS, type Lang } from './i18n/index.js';

export type QualitySetting = 'auto' | 'high' | 'medium' | 'low';
export type FogSetting = 'color' | 'mono';
export type StyleSetting = 'engrave' | 'toon';  // the whole frame's look: engraving (original) or cartoon
export type Settings = {
  sound: boolean;
  volume: number;  // 0..1, scales the master gain
  quality: QualitySetting;
  fog: FogSetting;
  style: StyleSetting;
  hints: boolean;  // the control-hint strip under the stage
  lockCursor: boolean;  // capture the pointer in a match (pointerLock.ts)
  lang: Lang | null;  // null: never chosen — the browser's languages decide (resolveLang)
};

export const DEFAULT_SETTINGS: Readonly<Settings> = { sound: true, volume: 1, quality: 'auto', fog: 'color', style: 'engrave', hints: true, lockCursor: true, lang: null };

/* 'auto' pins nothing and leaves the governor off — full quality, today's default (see matchView.ts). */
export const QUALITY_LEVEL: Record<QualitySetting, number | null> = { auto: null, high: 0, medium: 2, low: 4 };

const KEY = 'bestiarum-settings';
const LEGACY_MUTE_KEY = 'ashen-plate-mute';

export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>;

function browserStore(): KeyValueStore | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(v as T) ? v as T : fallback;

/** Anything missing or malformed falls back to its default, field by field. */
export function parseSettings(raw: string | null): Settings {
  let o: Record<string, unknown> = {};
  try { const p = raw ? JSON.parse(raw) : null; if (p && typeof p === 'object') o = p; } catch { /* corrupt: defaults */ }
  const d = DEFAULT_SETTINGS;
  return {
    sound: typeof o.sound === 'boolean' ? o.sound : d.sound,
    volume: typeof o.volume === 'number' && Number.isFinite(o.volume) ? Math.max(0, Math.min(1, o.volume)) : d.volume,
    quality: oneOf(o.quality, ['auto', 'high', 'medium', 'low'], d.quality),
    fog: oneOf(o.fog, ['color', 'mono'], d.fog),
    style: oneOf(o.style, ['engrave', 'toon'], d.style),
    hints: typeof o.hints === 'boolean' ? o.hints : d.hints,
    lockCursor: typeof o.lockCursor === 'boolean' ? o.lockCursor : d.lockCursor,
    lang: LANGS.includes(o.lang as Lang) ? o.lang as Lang : d.lang,
  };
}

/* Everything written this page session, per store: choices still apply when storage throws (private
   mode, blocked site data) — they just don't survive a reload. */
const NO_STORE = {};
const session = new WeakMap<object, Settings>();

export function readSettings(store: KeyValueStore | null = browserStore()): Settings {
  const cached = session.get(store ?? NO_STORE);
  if (cached) return { ...cached };
  let raw: string | null = null, legacyMuted = false;
  try {
    raw = store?.getItem(KEY) ?? null;
    legacyMuted = raw === null && store?.getItem(LEGACY_MUTE_KEY) === '1';  // the old M-key toggle's own key
  } catch { /* storage blocked: defaults */ }
  const s = parseSettings(raw);
  return legacyMuted ? { ...s, sound: false } : s;
}

export function writeSettings(s: Settings, store: KeyValueStore | null = browserStore()) {
  session.set(store ?? NO_STORE, { ...s });
  try { store?.setItem(KEY, JSON.stringify(s)); } catch { /* private mode, ignore */ }
}

export function updateSettings(patch: Partial<Settings>, store: KeyValueStore | null = browserStore()): Settings {
  const next = { ...readSettings(store), ...patch };
  writeSettings(next, store);
  return next;
}

/** `force`: the level to pin, or null for none; `governor`: run the automatic governor (?quality=auto only). */
export function resolveQuality(s: Settings, params: URLSearchParams): { force: number | null; governor: boolean } {
  const q = params.get('quality');
  if (q !== null) return { force: q === 'auto' || !Number.isFinite(Number(q)) ? null : Number(q), governor: q === 'auto' };
  return { force: QUALITY_LEVEL[s.quality], governor: false };
}

export function resolveFogColor(s: Settings, params: URLSearchParams): boolean {
  const f = params.get('fog');
  return f !== null ? f !== 'mono' : s.fog === 'color';
}

export function resolveStyle(s: Settings, params: URLSearchParams): StyleSetting {
  const v = params.get('style');
  return v === 'toon' || v === 'engrave' ? v : s.style;
}

/** The language to show: the player's choice, else the browser's preference (navigator.languages). */
export function resolveLang(s: Settings, languages: readonly string[] | undefined): Lang {
  return s.lang ?? detectLang(languages);
}
